import { useState, useMemo, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { VIEW_TYPE } from '@constants/microsites';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { SplitModal as Modal, PreviewWrapper } from '@components/SplitModal';
import { RadioButtonsGroup } from '@components/RadioButtonsGroup';
import { useTranslation, useYupValidationResolver, useSurveysOptions, useFormsOptions } from '@hooks';
import { micrositesService, menusService } from '@services';
import { handleApiError } from '@helpers/api';
import { LocationType } from '@constants/companies';
import PreviewMicrosite from '../PreviewMicrosite';
import General from './General';
import Landing from './Landing';
import { isLinkValid } from './helpers';

const FormWrapper = styled('div')({
    padding: '24px 16px',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    '@media (max-width: 768px)': {
        overflowY: 'initial',
        overflowX: 'initial',
    },
});

const RadioButtonsWrapper = styled('div')({
    overflowX: 'initial',
    height: 32,
    marginBottom: 24,
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
    '@media (max-width: 768px)': {
        overflowX: 'auto',
    },
});

const RadioButtons = styled(RadioButtonsGroup)({
    width: '100%',
    display: 'block',
    marginBottom: '24px',
    '& .MuiFormControlLabel-root': {
        height: '32px',
        padding: '0 4px',
    },
});

const Tab = {
    Settings: 0,
    Content: 1,
};

const EditModal = ({ modal, onClose, onCancel, selectedMicrosite, onDelete, onSave, account, isUser, brands }) => {
    const { t } = useTranslation();
    const [tab, setTab] = useState(Tab.Settings);
    const [brandLocationMicrosites, setBrandLocationMicrosites] = useState([]);
    const [isMicrositesLoaded, setIsMicrositesLoaded] = useState(false);
    const [menus, setMenus] = useState([]);
    const [isMenusLoaded, setIsMenusLoaded] = useState(false);
    const { getSurveysOptionsByLocation } = useSurveysOptions();
    const { getFormsOptionsByLocationId, getFormsOptionsByBrandId, isFormsLoaded } = useFormsOptions();

    const MicrositesSchema = yup.object().shape({
        name: yup.string().required('validation.required'),
        slug: yup
            .string()
            .required('validation.required')
            .not(selectedMicrosite?.restrictedNames || [], 'validation.takenURL')
            .matches(/^(?!unsubscribe+\b)/i, 'validation.takenURL')
            .matches(/^(?!q\/)/i, 'validation.takenURL')
            .matches(/^(?!s\/)/i, 'validation.takenURL')
            .matches(/^(?!c\/)/i, 'validation.takenURL')
            .min(8, 'validation.minLength8'),
        locationType: yup.string(),
        locationId: yup.string().when('locationType', {
            is: '0',
            then: schema => schema.required('validation.required'),
            otherwise: schema => schema.nullable(),
        }),
        brandId: yup.string().when('locationType', {
            is: '1',
            then: schema => schema.required('validation.required'),
            otherwise: schema => schema.nullable(),
        }),
        showLiveChat: yup.boolean(),
        liveChatChannelId: yup.string().when('showLiveChat', {
            is: true,
            then: schema => schema.required('validation.required'),
            otherwise: schema => schema.nullable(),
        }),
    });
    const resolver = useYupValidationResolver(MicrositesSchema);

    const {
        setValue,
        control,
        watch,
        handleSubmit,
        getValues,
        formState: { isValid, isDirty, isSubmitting, errors },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            name: '',
            locationId: '',
            brandId: '',
            locationType: '0',
            brandAccess: true,
            valuesAlreadySetToForm: false,
            slug: '',
            socialMediaEnable: false,
            logoUrl: '',
            links: {},
            showReviews: false,
            showLiveChat: false,
            liveChatChannelId: '',
            customDomainId: null,
        },
    });
    const links = watch('links');
    const locationType = watch('locationType');
    const locationId = watch('locationId');
    const brandId = watch('brandId');
    const isBrand = locationType === LOCATION_TYPE.BRAND;

    useEffect(() => {
        if (isBrand && brandId) {
            let cancelled = false;
            setBrandLocationMicrosites([]);
            setIsMicrositesLoaded(false);
            micrositesService
                .getBrandLocationMicrosites(brandId)
                .then(data => {
                    if (!cancelled) setBrandLocationMicrosites(data);
                })
                .catch(error => {
                    if (!cancelled) {
                        handleApiError(error);
                        setBrandLocationMicrosites([]);
                    }
                })
                .finally(() => {
                    if (!cancelled) setIsMicrositesLoaded(true);
                });
            return () => {
                cancelled = true;
            };
        } else {
            setBrandLocationMicrosites([]);
            setIsMicrositesLoaded(false);
        }
    }, [isBrand, brandId]);

    const surveysOptions = useMemo(
        () => (isBrand ? [] : getSurveysOptionsByLocation(locationId, s => ({ label: s.name, value: s.externalId }))),
        [locationId, isBrand],
    );

    const selectedLocation = account?.companies?.find(c => c.companyId === +locationId);
    const showMenuOption = !isBrand && [LocationType.Restaurant, LocationType.Hotel].includes(selectedLocation?.type);

    useEffect(() => {
        if (!showMenuOption || !locationId) {
            setMenus([]);
            setIsMenusLoaded(false);
            return undefined;
        }
        let cancelled = false;
        setIsMenusLoaded(false);
        menusService
            .getMenusForLocation(locationId)
            .then(data => {
                if (!cancelled) setMenus(data);
            })
            .catch(() => {
                if (!cancelled) setMenus([]);
            })
            .finally(() => {
                if (!cancelled) setIsMenusLoaded(true);
            });
        return () => {
            cancelled = true;
        };
    }, [showMenuOption, locationId]);

    const menusOptions = useMemo(
        () => menus.map(menu => ({ value: menu.id, label: menu.name || menu.internalName })),
        [menus],
    );

    const formsOptions = useMemo(
        () => (isBrand ? getFormsOptionsByBrandId(brandId) : getFormsOptionsByLocationId(locationId)),
        [isBrand, brandId, locationId, getFormsOptionsByBrandId, getFormsOptionsByLocationId],
    );

    const micrositeOptions = useMemo(
        () =>
            brandLocationMicrosites.map(m => ({
                value: m.id,
                label: m.locationName ? `${m.name} (${m.locationName})` : m.name,
            })),
        [brandLocationMicrosites],
    );

    const linkContext = useMemo(
        () => ({
            isBrand,
            surveysOptions,
            micrositeOptions,
            isMicrositesLoaded,
            menusOptions,
            isMenusLoaded,
            formsOptions,
            isFormsLoaded,
        }),
        [
            isBrand,
            surveysOptions,
            micrositeOptions,
            isMicrositesLoaded,
            menusOptions,
            isMenusLoaded,
            formsOptions,
            isFormsLoaded,
        ],
    );

    const invalidLinkKeys = useMemo(
        () => Object.keys(links).filter(key => links[key].some(link => !isLinkValid(link, linkContext))),
        [links, linkContext],
    );
    const allLinksValid = !invalidLinkKeys.length;

    useEffect(() => {
        if (selectedMicrosite) {
            setValue('name', selectedMicrosite.name, { shouldValidate: true });
            setValue('locationId', selectedMicrosite.locationId);
            setValue('slug', selectedMicrosite.slug, { shouldValidate: true });
            setValue('socialMediaEnable', selectedMicrosite.socialMediaEnable);
            setValue('logoUrl', selectedMicrosite.logoUrl);
            setValue('links', selectedMicrosite.links || {});
            setValue('showReviews', selectedMicrosite.showReviews);
            setValue('showLiveChat', !!selectedMicrosite.liveChatChannelId, { shouldValidate: true });
            setValue('liveChatChannelId', selectedMicrosite.liveChatChannelId || '', { shouldValidate: true });
            setValue('customDomainId', selectedMicrosite.customDomainId || null);
        }
    }, [selectedMicrosite, setValue]);

    const isView = modal.type === VIEW_TYPE.VIEW;
    const isCreate = modal.type === VIEW_TYPE.CREATE;
    const [isCancelling, setIsCancelling] = useState(false);

    const handleCancel = async () => {
        setIsCancelling(true);
        try {
            await onCancel();
        } finally {
            setIsCancelling(false);
        }
    };

    return (
        <Modal
            fullScreen
            isOpen={modal.open}
            handleClose={isCreate ? handleCancel : onClose}
            closeDisabled={isCancelling}
            isDirty={isDirty}
            title={t('MicrositesPage.modalTitle')}
            onPrimaryAction={isUser ? null : handleSubmit(onSave)}
            primaryActionText={t('buttons.save')}
            primaryActionDisabled={
                isSubmitting || isCancelling || !isValid || (!isCreate && !isDirty) || !allLinksValid
            }
            onAdditionalAction={isCreate || isUser ? null : onDelete}
            additionalTextButton={t('buttons.deleteSite')}
        >
            <FormProvider {...{ setValue, control, isValid, watch, getValues, errors }}>
                <FormWrapper>
                    <RadioButtonsWrapper>
                        <RadioButtons
                            value={tab}
                            onChange={setTab}
                            options={[
                                { value: Tab.Settings, label: t('common.settings') },
                                { value: Tab.Content, label: t('MicrositesPage.content') },
                            ]}
                        />
                    </RadioButtonsWrapper>
                    {tab === Tab.Settings && (
                        <General isView={isView} selectedMicrosite={selectedMicrosite} brands={brands} />
                    )}
                    {tab === Tab.Content && (
                        <Landing
                            isView={isView}
                            locale={account?.locale}
                            selectedMicrosite={selectedMicrosite}
                            linkContext={linkContext}
                            invalidLinkKeys={invalidLinkKeys}
                            showMenuOption={showMenuOption}
                        />
                    )}
                </FormWrapper>
                <PreviewWrapper>
                    <PreviewMicrosite slug={selectedMicrosite?.slug} />
                </PreviewWrapper>
            </FormProvider>
        </Modal>
    );
};

export default EditModal;
